// NOAA's fractional-year solar position equations. Geometric solar centre,
// no atmospheric refraction or terrain horizon. Planning approximation, not SPA.
const R = Math.PI / 180, D = 180 / Math.PI;
export function solarPosition(timestamp, latitude, longitude, north = 270) {
  const date = new Date(timestamp);
  if (!timestamp || !Number.isFinite(+date) || latitude == null || longitude == null || !Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  const year = date.getUTCFullYear(), days = (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / 86400000;
  const day = Math.floor((+date - Date.UTC(year, 0, 1)) / 86400000) + 1;
  const hour = date.getUTCHours() + date.getUTCMinutes() / 60 + date.getUTCSeconds() / 3600;
  const g = 2 * Math.PI / days * (day - 1 + (hour - 12) / 24);
  const eq = 229.18 * (0.000075 + 0.001868 * Math.cos(g) - 0.032077 * Math.sin(g) - 0.014615 * Math.cos(2 * g) - 0.040849 * Math.sin(2 * g));
  const dec = 0.006918 - 0.399912 * Math.cos(g) + 0.070257 * Math.sin(g) - 0.006758 * Math.cos(2 * g) + 0.000907 * Math.sin(2 * g) - 0.002697 * Math.cos(3 * g) + 0.00148 * Math.sin(3 * g);
  const ha = (((hour * 60 + eq + 4 * longitude) % 1440 + 1440) % 1440 / 4 - 180) * R, lat = latitude * R;
  const elevation = Math.asin(Math.max(-1, Math.min(1, Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(ha)))) * D;
  const azimuth = (Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat)) * D + 180 + 360) % 360;
  const angle = (north + azimuth) * R, el = elevation * R;
  return { azimuth, elevation, direction: { x: Math.cos(angle) * Math.cos(el), y: Math.sin(angle) * Math.cos(el), z: Math.sin(el) },
    method: 'NOAA fractional-year approximation; no refraction' };
}

export function localDateTime(timestamp, timeZone) {
  if (!timestamp) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(new Date(timestamp)).map(p => [p.type, p.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** Resolve civil time explicitly. Reject DST gaps/folds instead of guessing. */
export function resolveLocalTime(local, timeZone) {
  if (!/^\d{4}-\d\d-\d\dT\d\d:\d\d$/.test(local)) throw new Error('Enter a complete date and time.');
  const guess = Date.parse(local + 'Z');
  if (!Number.isFinite(guess)) throw new Error('Invalid date and time.');
  const offsets = new Set();
  for (const delta of [-36, -12, 0, 12, 36]) {
    const utc = guess + delta * 3600000, shown = Date.parse(localDateTime(new Date(utc).toISOString(), timeZone) + 'Z');
    offsets.add(shown - utc);
  }
  const matches = [...offsets].map(o => new Date(guess - o).toISOString()).filter(t => localDateTime(t, timeZone) === local);
  if (matches.length !== 1) throw new Error(matches.length ? 'This local time occurs twice at the daylight-saving change. Choose a time outside that repeated hour.' : 'This local time does not exist at the daylight-saving change. Choose another time.');
  return matches[0];
}
